/**
 * powerUpCosts — the Pokémon GO power-up price list (2026-08-26).
 *
 * One power-up = +0.5 level. Costs depend only on the level the power-up is
 * BOUGHT AT, in flat tiers:
 *   - Stardust rises every 2 levels (200 at L1 → 15,000 at L49).
 *   - Regular candy rises in coarser bands and stops at level 40.
 *   - Candy XL takes over from level 40 (10 → 20 per step; 296 total 40→50).
 *
 * Verified against Poké Genie's output for a level-26 15/11/6 Cyndaquil
 * (2026-08-26 side-by-side): 26→36 = 114,000 dust / 116 candy; 26→32.5 =
 * 64,000 / 58; 26→50 = 436,000 / 214 regular + 296 XL. (Poké Genie's candy
 * figures on EVOLVED rows also fold in the evolution candy — 25/125 for the
 * Cyndaquil line — which this table deliberately does not: evolution cost is
 * per-species Game Master data the app does not carry yet.)
 */

/** Stardust per power-up, tier i covering levels [1+2i, 3+2i). */
const DUST_TIERS = [
  200, 400, 600, 800, 1000, 1300, 1600, 1900, 2200, 2500, 3000, 3500, 4000,
  4500, 5000, 6000, 7000, 8000, 9000, 10000, 11000, 12000, 13000, 14000, 15000,
];

/** Stardust for the power-up bought at `level` (levels 1 .. 49.5). */
export function dustAtLevel(level: number): number {
  const tier = Math.floor((level - 1) / 2);
  return DUST_TIERS[Math.max(0, Math.min(DUST_TIERS.length - 1, tier))];
}

/** Regular candy for the power-up bought at `level`; 0 from level 40 up. */
export function candyAtLevel(level: number): number {
  if (level >= 40) return 0;
  if (level >= 39) return 15;
  if (level >= 37) return 12;
  if (level >= 35) return 10;
  if (level >= 33) return 8;
  if (level >= 31) return 6;
  if (level >= 26) return 4;
  if (level >= 21) return 3;
  if (level >= 11) return 2;
  return 1;
}

/** Candy XL for the power-up bought at `level`; 0 below level 40. */
export function candyXlAtLevel(level: number): number {
  if (level < 40) return 0;
  if (level < 42) return 10;
  if (level < 44) return 12;
  if (level < 46) return 15;
  if (level < 48) return 17;
  return 20;
}

export interface PowerUpCost {
  dust: number;
  candy: number;
  candyXl: number;
}

/**
 * The record states that change what a power-up costs in GO:
 *   - Lucky halves the STARDUST (candy is unaffected).
 *   - Shadow costs 1.2x stardust AND candy; Purified 0.9x of both. The two
 *     are mutually exclusive states, and shadow wins if a bad record carries
 *     both. Lucky stacks multiplicatively with purified (0.45x dust).
 * Candy is rounded UP per individual half-level power-up, which is why a low
 * level Purified still pays 1 candy a step. Dust needs no rounding: every
 * tier is a multiple of 100, so 0.5x / 0.9x / 1.2x / 0.45x stay whole.
 */
export interface PowerUpModifiers {
  lucky?: boolean;
  shadow?: boolean;
  purified?: boolean;
}

function dustMultiplier(m?: PowerUpModifiers): number {
  let mult = 1;
  if (m?.lucky) mult *= 0.5;
  if (m?.shadow) mult *= 1.2;
  else if (m?.purified) mult *= 0.9;
  return mult;
}

function candyMultiplier(m?: PowerUpModifiers): number {
  return m?.shadow ? 1.2 : m?.purified ? 0.9 : 1;
}

/**
 * Total cost to power up from `fromLevel` to `toLevel` (both in half-level
 * steps; inputs are snapped to the nearest half). `toLevel` at or below
 * `fromLevel` costs nothing. Levels above 50 are clamped (51 is Best Buddy
 * boost, not a power-up). `modifiers` applies the Lucky / Shadow / Purified
 * pricing (2026-08-30 report: a Lucky Eevee's Master League target quoted
 * full-price dust).
 */
export function powerUpCost(
  fromLevel: number,
  toLevel: number,
  modifiers?: PowerUpModifiers,
): PowerUpCost {
  const snap = (l: number) => Math.round(l * 2) / 2;
  const from = Math.max(1, snap(fromLevel));
  const to = Math.min(50, snap(toLevel));
  const dustMult = dustMultiplier(modifiers);
  const candyMult = candyMultiplier(modifiers);
  const out: PowerUpCost = { dust: 0, candy: 0, candyXl: 0 };
  for (let l = from; l < to; l += 0.5) {
    // Per-step application, the way GO charges each power-up: candy rounds up
    // per step, dust is exact (see PowerUpModifiers).
    out.dust += Math.round(dustAtLevel(l) * dustMult);
    out.candy += Math.ceil(candyAtLevel(l) * candyMult);
    out.candyXl += Math.ceil(candyXlAtLevel(l) * candyMult);
  }
  return out;
}

/** "436,000" → "436k"; keeps sub-10k values exact ("8,000" → "8k" reads
 * wrong at a glance below 10k, so those print in full). */
export function formatDust(dust: number): string {
  if (dust >= 10_000) return `${Math.round(dust / 1000)}k`;
  return dust.toLocaleString();
}
